import { Button, DateBox, TextBox } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import { formatMessage } from 'devextreme/localization';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import * as dayjs from 'dayjs';
import DataGridMVP from '../../DataGridMVP';
import SearchBar from '../../SearchBar/SearchBar';
import styles from './actsAdmin.module.scss';
import columnDataGrid from './columnDataGrid';
import HeaderButtonMVP from '../../HeaderButtonMVP';
import filterConfig from './filterConfig';
import PopupMVP from '../../PopupMVP';

const signDataDefault = {
  confirmStatus: 1,
  docNumId: null,
  signTime: null,
  signer: null
};

const ActsAdmin = ({
  plantId,
  getActs,
  signActs,
  adminActsReducer,
  adminMessagesReducer,
  resetMessages
}) => {
  const [dataGridInstance, setDataGridInstance] = useState();
  const [inputValue, setInputValue] = useState('');
  const [filterData, setFilterData] = useState({});
  const [focusedRowData, setFocusedRowData] = useState(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
  const [headButtVisibol, setHeadButtVisibol] = useState({ display: 'none' });
  const [signData, setSignData] = useState(signDataDefault);
  const [visiblePopupSign, setVisiblePopupSign] = useState(false);

  useEffect(() => {
    if (focusedRowData) {
      setHeadButtVisibol({ display: 'flex', top: '55px' });
    } else {
      setHeadButtVisibol({ display: 'none' });
    }
  }, [focusedRowData]);

  useEffect(() => {
    getActs({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  useEffect(() => {
    getActs(filterData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminActsReducer.actsSign.okRequest,
    adminMessagesReducer.messagesReset.okRequest,
    getActs
  ]);

  useEffect(() => {
    if (focusedRowData && adminActsReducer.actsList) {
      const updatedRow = adminActsReducer.actsList.find((item) => (
        item.docNumId === focusedRowData.docNumId
      ));
      if (updatedRow) {
        setFocusedRowData(updatedRow);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminActsReducer.actsList]);

  const onResetFilter = useCallback(() => {
    getActs({});
  }, [getActs]);

  const onSaveFilter = useCallback(() => {
    getActs(filterData);
  }, [filterData, getActs]);

  const onClickSearch = useCallback(() => {
    getActs(filterData);
  }, [filterData, getActs]);

  const onFocusedRowChanged = useCallback(({ row }) => {
    if (row) {
      const oneHours = 60 * 60 * 1000;
      setSignData({
        ...signDataDefault,
        docNumId: row.data.docNumId,
        signer: signData.signer,
        signTime: row.data.inSignDatetime && dayjs(Date.parse(row.data.inSignDatetime) + oneHours).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
      });
      setFocusedRowData(row.data);
      setFocusedRowIndex(row.rowIndex);
    } else {
      setSignData(signDataDefault);
      setFocusedRowData(null);
    }
  }, [signData]);

  const renderColumnDataGrid = useMemo(
    () =>
      columnDataGrid.map((item) => (
        <Column
          dataField={item.dataField}
          caption={formatMessage('textTableAdminActs')[item.dataField]}
          visible={!item.notVisible}
          allowFiltering
          cellRender={item.cellRender}
          key={item.dataField}
          dataType={item.dataType}
          cssClass={item.cssClass}
        />
      )),
    []
  );

  const onSignClick = useCallback(() => {
    setVisiblePopupSign(true);
  }, []);

  const onSignCancelClick = useCallback(() => {
    const { docNumId } = focusedRowData;
    signActs({
      docNumId,
      confirmStatus: 0,
      signTime: dayjs(new Date().toLocaleString('en-US')).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
    });
  }, [focusedRowData, signActs]);

  const onSignRetryClick = useCallback(() => {
    const { eventId } = focusedRowData;
    resetMessages({ msgCounter: eventId });
  }, [focusedRowData, resetMessages]);

  const onSignPopupHiding = useCallback(() => {
    setVisiblePopupSign(false);
  }, []);

  const onSignConfirmClick = useCallback(() => {
    signActs(signData);
    setVisiblePopupSign(false);
  }, [signActs, signData]);

  return (
    <>
      <SearchBar
        inputValue={inputValue}
        setInputValue={setInputValue}
        filterData={filterData}
        setFilterData={setFilterData}
        dataGridInstance={dataGridInstance}
        contextSearch
        onResetFilter={onResetFilter}
        onSaveFilter={onSaveFilter}
        filterConfig={filterConfig}
        onClickSearch={onClickSearch}
      />
      <HeaderButtonMVP headButtVisibol={headButtVisibol}>
        <Button
          icon="close"
          type={'default'}
          stylingMode={'contained'}
          onClick={() => {
            setFocusedRowData(null);
            setFocusedRowIndex(-1);
          }}
        />
        <Button
          text={formatMessage('administrationTabs').buttons.sign}
          disabled={!(focusedRowData && focusedRowData.docStatus === 3)}
          icon="edit"
          type={'default'}
          stylingMode={'contained'}
          onClick={onSignClick}
        />
        <Button
          text={formatMessage('administrationTabs').buttons.cancelSign}
          disabled={!(focusedRowData && focusedRowData.docStatus === 4)}
          icon="close"
          type={'default'}
          stylingMode={'contained'}
          onClick={onSignCancelClick}
        />
        <Button
          disabled={!(focusedRowData && focusedRowData.docStatus === 3)}
          text={formatMessage('administrationTabs').buttons.resend}
          icon="refresh"
          type={'default'}
          stylingMode={'contained'}
          onClick={onSignRetryClick}
        />
      </HeaderButtonMVP>
      <div className={styles.dataGridWrapper}>
        <DataGridMVP
          dataSource={adminActsReducer.actsList}
          setDataGridInstance={setDataGridInstance}
          keyExpr="docNumId"
          setStateStoringName={'dataGridActsAdmin'}
          isDataLoading={adminActsReducer.actsGet.loading}
          visibleButtonDateRangePicker={false}
          tText={formatMessage('textPeriod')}
          focusedRowEnabled
          focusedRowIndex={focusedRowIndex}
          // focusedRowIndex={focusedRowData && focusedRowData.docNumId}
          onFocusedRowChanged={onFocusedRowChanged}
          selectionMode="none"
          visibleSearchPanel={false}
          visibleInfoIcon={false}
          visibleSummary={false}
          filterDataGrid
        >
          {renderColumnDataGrid}
        </DataGridMVP>
      </div>
      <PopupMVP
        visible={visiblePopupSign}
        onHiding={onSignPopupHiding}
        title="Подписать"
      >
        <div className="formData">
          <TextBox
            onValueChanged={({ value }) =>
              setSignData({ ...signData, signer: value })}
            placeholder={formatMessage('administrationTabs').signPopup.enterName}
            stylingMode="underlined"
            value={signData.signer}
          />
          <DateBox
            placeholder={formatMessage('administrationTabs').signPopup.enterDate}
            value={signData.signTime && signData.signTime.slice(0, -1)}
            displayFormat="dd.MM.yyyy HH:mm"
            onValueChanged={({ value }) =>
              setSignData({ ...signData, signTime: value && dayjs(value).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') })}
            stylingMode="underlined"
            type="datetime"
          />
          <div className="formButton">
            <Button
              text={formatMessage('textButton').cancel}
              type="danger"
              stylingMode="text"
              onClick={onSignPopupHiding}
            />
            <Button
              type="default"
              stylingMode="contained"
              text={formatMessage('textButton').confirm}
              width={200}
              onClick={onSignConfirmClick}
            />
          </div>
        </div>
      </PopupMVP>
    </>
  );
};

export default React.memo(ActsAdmin);
